import { Button, Typography } from '@pesto/ui';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

interface ErrorPageProps {
  error?: string;
}

const fadeInAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const scaleAnimation = {
  initial: { scale: 0.9 },
  animate: { scale: 1 },
  transition: { delay: 0.2 },
};

const shakeAnimation = {
  animate: {
    rotate: [0, -5, 5, -5, 0],
    transition: { duration: 0.5, delay: 0.5 },
  },
};

const buttonAnimation = {
  whileHover: { scale: 1.05 },
  whileTap: { scale: 0.95 },
};

const ReloadIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const ErrorPage = ({ error }: ErrorPageProps) => {
  return (
    <motion.div
      {...fadeInAnimation}
      className="bg-background flex min-h-screen flex-col items-center justify-center p-4"
    >
      <motion.div
        {...scaleAnimation}
        className="border-border/70 bg-card mx-auto w-full max-w-2xl space-y-8 rounded-xl border p-8 text-center shadow-sm"
      >
        <motion.div {...shakeAnimation}>
          <AlertTriangle className="text-destructive mx-auto mb-6 h-24 w-24" />
        </motion.div>

        <Typography variant="heroSmall" className="text-foreground text-4xl font-bold md:text-5xl">
          Whoops! An error occurred.
        </Typography>

        <div className="space-y-4">
          <Typography variant="body" className="text-muted-foreground text-lg">
            Something went wrong. Please reload the page or contact support if the issue continues.
          </Typography>

          {error && (
            <div className="bg-muted rounded-lg p-4">
              <Typography variant="body" className="text-muted-foreground font-mono text-sm">
                Error: {error}
              </Typography>
            </div>
          )}
        </div>

        <motion.div {...buttonAnimation}>
          <Button size="lg" variant="primary" onClick={() => window.location.reload()} className="gap-2">
            <ReloadIcon />
            Reload Page
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default ErrorPage;
